import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { useCancelScheduleMutation } from "@/pages/Private/redux/schedule/schedule.api";
import { useSnackBar } from "@/utils/useSnackbar";

/**
 * Confirm-and-cancel flow for a pending scheduled message. Self-contained like its siblings
 * `usePauseScheduleModal` and `useEditScheduleModal` — it owns its snackbar and is rendered by the
 * bubble's controls, so the thread's host has nothing to thread through (PREC-4220). The slice
 * marks the schedule as canceled on success, so the conversation bubble re-renders in place
 * without a refetch.
 */
export const useCancelScheduleModal = () => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	// Holds the schedule id pending cancellation; null when the modal is closed.
	const [scheduleIdToCancel, setScheduleIdToCancel] = useState<number | null>(null);
	const [cancelSchedule, { isLoading: isCancellingSchedule }] = useCancelScheduleMutation();

	const handleConfirmCancelSchedule = useCallback(async () => {
		if (scheduleIdToCancel === null) {
			return;
		}

		try {
			await cancelSchedule(scheduleIdToCancel).unwrap();
			handleOpenSnackbar(t("outbox.cancelSchedule.success"));
		} catch {
			handleOpenSnackbar(t("outbox.cancelSchedule.error"));
		} finally {
			setScheduleIdToCancel(null);
		}
	}, [scheduleIdToCancel, cancelSchedule, handleOpenSnackbar, t]);

	const renderCancelScheduleModal = () => (
		<>
			{scheduleIdToCancel !== null && (
				<Modal
					handleClose={() => setScheduleIdToCancel(null)}
					handleSave={handleConfirmCancelSchedule}
					isLoading={isCancellingSchedule}
					isOpened={scheduleIdToCancel !== null}
					size="xs"
					submitButtonColor="error"
					submitButtonText={t("outbox.cancelSchedule.action")}
					title={t("outbox.cancelSchedule.title")}
				>
					<div className="text-sm text-gray-700">{t("outbox.cancelSchedule.confirmation")}</div>
				</Modal>
			)}
			{renderMessageSnackbar()}
		</>
	);

	return { requestCancelSchedule: setScheduleIdToCancel, renderCancelScheduleModal };
};
